import backlogIcon from '@/assets/icons/linear/status/backlog-status.svg';
import holdIcon from '@/assets/icons/linear/status/hold-status.svg';
import newProjectIcon from '@/assets/icons/linear/status/new-project-status.svg';
import requirementsIcon from '@/assets/icons/linear/status/requirements-status.svg';
import readyIcon from '@/assets/icons/linear/status/ready-status.svg';
import scheduledIcon from '@/assets/icons/linear/status/scheduled-status.svg';
import buildingIcon from '@/assets/icons/linear/status/building-status.svg';
import uatIcon from '@/assets/icons/linear/status/uat-status.svg';
import goLiveIcon from '@/assets/icons/linear/status/go-live-status.svg';
import completedIcon from '@/assets/icons/linear/status/completed-status.svg';
import canceledIcon from '@/assets/icons/linear/status/canceled-status.svg';
import noPriorityIcon from '@/assets/icons/linear/priority/no-priority.svg';
import lowPriorityIcon from '@/assets/icons/linear/priority/low.svg';
import mediumPriorityIcon from '@/assets/icons/linear/priority/medium.svg';
import highPriorityIcon from '@/assets/icons/linear/priority/high.svg';
import urgentPriorityIcon from '@/assets/icons/linear/priority/urgent.svg';

export const STATUS_OPTIONS = [
  { label: 'Backlog', value: 'Backlog', icon: backlogIcon },
  { label: 'On Hold', value: 'On Hold', icon: holdIcon },
  { label: 'Client Setup Pending', value: 'Client Setup Pending', icon: holdIcon },
  { label: 'New Project', value: 'New Project', icon: newProjectIcon },
  { label: 'Client Setup in Progress', value: 'Client Setup in Progress', icon: requirementsIcon },
  { label: 'Requirements Verification', value: 'Requirements Verification', icon: requirementsIcon },
  { label: 'Ready to Start', value: 'Ready to Start', icon: readyIcon },
  { label: 'Scheduled', value: 'Scheduled', icon: scheduledIcon },
  { label: 'Building', value: 'Building', icon: buildingIcon },
  { label: 'UAT', value: 'UAT', icon: uatIcon },
  { label: 'Go Live', value: 'Go Live', icon: goLiveIcon },
  { label: 'Completed', value: 'Completed', icon: completedIcon },
  { label: 'Canceled', value: 'Canceled', icon: canceledIcon },
  { label: 'Returned to Requirements', value: 'Returned to Requirements', icon: requirementsIcon }
];

export const STATUS_COLORS = {
  Backlog: '#7FA085',
  'On Hold': '#D49B7F',
  'Client Setup Pending': '#D49B7F',
  'Awaiting Client Readiness': '#9BB83A',
  'Client Setup in Progress': '#9BB83A',
  'New Project': '#B5AF00',
  Requirements: '#9BB83A',
  'Requirements Verification': '#9BB83A',
  'Ready to Start': '#4D9DB0',
  Scheduled: '#5BC4DB',
  Building: '#00B3CC',
  UAT: '#0099E6',
  'Go Live': '#4169E1',
  Completed: '#B347D9',
  Canceled: '#8A8F98',
  'Returned to Requirements': '#7C6FBD'
};

export const getStatusStyle = (status) => {
  const color = STATUS_COLORS[status];

  if (!color) {
    return {};
  }

  return {
    backgroundColor: 'transparent',
    border: 'none',
    color
  };
};

export const ISSUE_STATUS_OPTIONS = [
  { label: 'Backlog', value: 'Backlog', icon: backlogIcon, description: 'Idea captured but not groomed or estimated yet.' },
  { label: 'Blocked', value: 'Blocked', icon: holdIcon, description: 'Workflow is blocked by a dependency, missing info, or an external issue.' },
  { label: 'Todo', value: 'Todo', icon: newProjectIcon, description: 'Ready to be picked up by engineering.' },
  { label: 'On Credit', value: 'On Credit', icon: newProjectIcon, description: 'Workflow on credit.' },
  { label: 'Requirements → Client', value: 'Requirements → Client', icon: requirementsIcon, description: 'Waiting on client input or formal sign-off (e.g., blueprint approval, credentials, clarifications).' },
  { label: 'Ready', value: 'Ready', icon: readyIcon, description: 'Fully groomed—scope frozen, access verified, test cases defined—dev can start immediately.' },
  { label: 'In Dev', value: 'In Dev', icon: buildingIcon, description: 'Developers actively building/configuring the workflow.' },
  { label: 'QA / Internal Review', value: 'QA / Internal Review', icon: scheduledIcon, description: 'Code & config finished; undergoing unit/integration tests, code review, or peer QA.' },
  { label: 'UAT / Client Review', value: 'UAT / Client Review', icon: uatIcon, description: 'Deployed to staging; client is executing test cases and logging punch-list items.' },
  { label: 'Ready for Go-Live', value: 'Ready for Go-Live', icon: goLiveIcon, description: 'UAT passed, punch-list at zero; deployment checklist complete and release window scheduled.' },
  { label: 'Done', value: 'Done', icon: completedIcon, description: 'Workflow live in production, documentation/handoff delivered, and client has accepted.' },
  { label: 'Live', value: 'Live', icon: completedIcon, description: 'Workflow is completed and live.' },
  { label: 'Canceled', value: 'Canceled', icon: canceledIcon, description: 'Canceled and no longer being worked.' }
];

export const ISSUE_STATUS_DESCRIPTIONS = Object.fromEntries((ISSUE_STATUS_OPTIONS || []).map((n) => [n.value, n.description]));

export const ISSUE_STATUS_COLORS = {
  Backlog: '#7FA085',
  Blocked: '#D49B7F',
  Todo: '#B5AF00',
  'On Credit': '#B5AF00',
  'Requirements → Client': '#9BB83A',
  Ready: '#4D9DB0',
  'In Dev': '#00B3CC',
  'QA / Internal Review': '#5BC4DB',
  'UAT / Client Review': '#0099E6',
  'Ready for Go-Live': '#4169E1',
  Done: '#B347D9',
  Live: '#B347D9',
  Canceled: '#8A8F98'
};

export const getIssueStatusStyle = (status) => {
  const color = ISSUE_STATUS_COLORS[status];

  if (!color) {
    return {};
  }

  return {
    backgroundColor: 'transparent',
    border: 'none',
    color
  };
};

const healthDotIcon = (hex) => `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><circle cx="6" cy="6" r="6" fill="${hex}"/></svg>`)}`;

export const PROJECT_HEALTH_OPTIONS = [
  { label: 'Not Set', value: 'not_set', color: '#dce3e8', icon: healthDotIcon('#dce3e8'), description: 'Health status has not been set' },
  { label: 'Excellent', value: 'green', color: '#16a34a', icon: healthDotIcon('#16a34a'), description: 'Client very happy, delivery smooth, no blockers' },
  { label: 'Healthy', value: 'blue', color: '#2563eb', icon: healthDotIcon('#2563eb'), description: 'Client satisfied, project progressing normally' },
  { label: 'Internal Blocker', value: 'yellow', color: '#ca8a04', icon: healthDotIcon('#ca8a04'), description: 'Progress paused due to something on our side' },
  { label: 'Client Concern', value: 'amber', color: '#d97706', icon: healthDotIcon('#d97706'), description: 'Not blocked, but momentum is slow and client sentiment is not positive' },
  { label: 'Client Blocker', value: 'orange', color: '#e57a00', icon: healthDotIcon('#e57a00'), description: 'Waiting on client input/action to proceed' },
  { label: 'On Hold', value: 'gray', color: '#8A8F98', icon: healthDotIcon('#8A8F98'), description: 'Project intentionally paused by client' },
  { label: 'Scope Change', value: 'purple', color: '#9333ea', icon: healthDotIcon('#9333ea'), description: 'Direction shifting or requirements unclear' },
  { label: 'Stalled / At Risk', value: 'red', color: '#dc2626', icon: healthDotIcon('#dc2626'), description: 'Project not moving, escalation required' }
];

export const PROJECT_HEALTH_COLORS = {
  not_set: '#dce3e8',
  green: '#16a34a',
  blue: '#2563eb',
  yellow: '#ca8a04',
  amber: '#d97706',
  orange: '#e57a00',
  gray: '#8A8F98',
  purple: '#9333ea',
  red: '#dc2626'
};

export const getProjectHealthStyle = (health) => {
  const color = PROJECT_HEALTH_COLORS[health];

  if (!color) {
    return {};
  }

  return {
    backgroundColor: 'transparent',
    border: 'none',
    color
  };
};

export const PRIORITY_OPTIONS = [
  { label: 'No Priority', value: '0', icon: noPriorityIcon },
  { label: 'Urgent', value: '1', icon: urgentPriorityIcon },
  { label: 'High', value: '2', icon: highPriorityIcon },
  { label: 'Medium', value: '3', icon: mediumPriorityIcon },
  { label: 'Low', value: '4', icon: lowPriorityIcon }
];

export const PRIORITY_COLORS = {
  0: '#8A8F98',
  1: '#ff7575',
  2: '#e44d26',
  3: '#f3ac3d',
  4: '#00b3cc'
};

export const getPriorityStyle = (priority) => {
  const color = PRIORITY_COLORS[priority];

  if (!color) {
    return {};
  }

  return {
    backgroundColor: 'transparent',
    border: 'none',
    color
  };
};

export const ISSUE_STATUS_ORDER = [
  // Group 1: Client Requirements
  'Requirements → Client',
  // Group 2: Development Pipeline
  'Ready',
  'In Dev',
  'QA / Internal Review',
  // Group 3: Client Review
  'UAT / Client Review',
  // Group 4: Completed
  'Ready for Go-Live',
  'Done',
  'Live',
  // Group 5: Other (less important, grayed out)
  'Backlog',
  'Blocked',
  'Todo',
  'On Credit',
  'Canceled'
];

export const ISSUE_STATUS_GROUP_BOUNDARIES = [
  'Requirements → Client', // End of Group 1
  'QA / Internal Review', // End of Group 2
  'UAT / Client Review', // End of Group 3
  'Live' // End of Group 4
  // Group 5 has no boundary (last group)
];

export const ISSUE_STATUS_OTHER_GROUP = ['Backlog', 'Blocked', 'Todo', 'On Credit', 'Canceled'];

// Format average time in status for display (e.g., "2 weeks", "3 days")
export const formatAverageStatusTime = (days) => {
  if (days == null || days <= 0) return null;

  if (days < 1) {
    return '< 1 day';
  }

  if (days < 7) {
    const roundedDays = Math.round(days);
    return roundedDays === 1 ? '1 day' : `${roundedDays} days`;
  }

  const weeks = Math.round(days / 7);
  if (weeks < 4) {
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }

  const months = Math.round(days / 30);
  return months === 1 ? '1 month' : `${months} months`;
};
